import { useEffect, useState } from 'react';
import AppLayout from '@/components/feature/AppLayout';
import { useAuth } from '@/context/AuthContext';

interface ConfigCategory {
  id: string;
  name: string;
  code: string;
  description: string;
  color: string;
  active: boolean;
}

interface ConfigStatus {
  id: string;
  name: string;
  code: string;
  description: string;
  color: string;
  active: boolean;
}

interface RiskRule {
  id: string;
  label: string;
  minScore: number;
  maxScore: number;
  criteria: string[];
  color: string;
  bgColor: string;
  active: boolean;
}

const API_BASE = 'https://guardiancloudcare.com/backend/api';

export default function AdminSystemConfigPage() {
  const [caseCategories, setCaseCategories] = useState<ConfigCategory[]>([]);
  const [statusTypes, setStatusTypes] = useState<ConfigStatus[]>([]);
  const [riskRules, setRiskRules] = useState<RiskRule[]>([]);

  const { user: currentUser } = useAuth();

  const [configTab, setConfigTab] = useState<
    'categories' | 'statuses' | 'risk'
  >('categories');

  const [showCatModal, setShowCatModal] = useState(false);

  const [catForm, setCatForm] = useState({
    name: '',
    code: '',
    description: '',
    color: 'bg-teal-500',
  });

  const [editRiskId, setEditRiskId] = useState<string | null>(null);
  const [editRiskCriteria, setEditRiskCriteria] = useState<string[]>([]);
  const [editRiskScore, setEditRiskScore] = useState<number>(0);
  const [newCriterion, setNewCriterion] = useState('');

  const [saved, setSaved] = useState(false);
  const [loading, setLoading] = useState(true);

  const showSavedMessage = () => {
    setSaved(true);

    setTimeout(() => {
      setSaved(false);
    }, 2500);
  };

  const createAuditLog = async (
    action: string,
    description: string
  ) => {
    try {
      await fetch(`${API_BASE}/create_audit_log.php`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          uid: currentUser?.uid || '',
          user_name: currentUser?.name || '',
          user_role: currentUser?.role || '',
          action,
          module: 'System Configuration',
          description,
        }),
      });
    } catch (err) {
      console.error('Audit log error:', err);
    }
  };

  const saveConfig = async (
    configKey: string,
    configValue: any
  ) => {
    try {
      const res = await fetch(
        `${API_BASE}/update_system_config.php`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            config_key: configKey,
            config_value: configValue,
          }),
        }
      );

      const data = await res.json();

      if (data.success) {
        setSaved(true);

        let description = '';

        switch (configKey) {
          case 'case_categories':
            description =
              'Updated case category configuration';
            break;

          case 'status_types':
            description =
              'Updated case status type configuration';
            break;

          case 'risk_rules':
            description =
              'Updated risk level configuration';
            break;

          default:
            description =
              `Updated system configuration: ${configKey}`;
        }

        await createAuditLog('Update', description);

        setTimeout(() => {
          setSaved(false);
        }, 2000);

        return true;
      }

      alert(
        data.error ||
          'Failed to save configuration'
      );

      return false;
    } catch (err) {
      console.error(
        'Save configuration error:',
        err
      );

      alert(
        'Unable to connect to the server.'
      );

      return false;
    }
  };

  useEffect(() => {
    const loadConfiguration = async () => {
      try {
        setLoading(true);

        const response = await fetch(
          `${API_BASE}/get_system_config.php`
        );

        if (!response.ok) {
          throw new Error(
            `HTTP error ${response.status}`
          );
        }

        const result = await response.json();

        if (
          !result.success ||
          !Array.isArray(result.data)
        ) {
          throw new Error(
            result.error ||
              'Invalid configuration response'
          );
        }

        result.data.forEach((config: any) => {
          if (
            config.config_key ===
            'case_categories'
          ) {
            const categories: ConfigCategory[] =
              Array.isArray(
                config.config_value
              )
                ? config.config_value.map(
                    (
                      item: any,
                      index: number
                    ) => {
                      if (
                        typeof item ===
                        'string'
                      ) {
                        return {
                          id: `category-${index}`,
                          name: item,
                          code: item
                            .toUpperCase()
                            .replace(
                              /[^A-Z0-9]+/g,
                              '_'
                            )
                            .replace(
                              /^_+|_+$/g,
                              ''
                            ),
                          description:
                            'Available case category',
                          color:
                            'bg-teal-500',
                          active: true,
                        };
                      }

                      return {
                        id: `category-${index}`,
                        name:
                          item.name ||
                          '',
                        code:
                          item.code ||
                          String(
                            item.name ||
                              ''
                          )
                            .toUpperCase()
                            .replace(
                              /[^A-Z0-9]+/g,
                              '_'
                            )
                            .replace(
                              /^_+|_+$/g,
                              ''
                            ),
                        description:
                          item.description ||
                          'Available case category',
                        color:
                          item.color ||
                          'bg-teal-500',
                        active:
                          item.active !==
                          false,
                      };
                    }
                  )
                : [];

            setCaseCategories(
              categories
            );
          }

          if (
            config.config_key ===
            'status_types'
          ) {
            const statuses: ConfigStatus[] =
              Array.isArray(
                config.config_value
              )
                ? config.config_value.map(
                    (
                      item: any,
                      index: number
                    ) => {
                      if (
                        typeof item ===
                        'string'
                      ) {
                        return {
                          id: `status-${index}`,
                          name: item,
                          code: item
                            .toUpperCase()
                            .replace(
                              /[^A-Z0-9]+/g,
                              '_'
                            )
                            .replace(
                              /^_+|_+$/g,
                              ''
                            ),
                          description:
                            'Available case status',
                          color:
                            'bg-teal-500',
                          active: true,
                        };
                      }

                      return {
                        id: `status-${index}`,
                        name:
                          item.name ||
                          '',
                        code:
                          item.code ||
                          String(
                            item.name ||
                              ''
                          )
                            .toUpperCase()
                            .replace(
                              /[^A-Z0-9]+/g,
                              '_'
                            )
                            .replace(
                              /^_+|_+$/g,
                              ''
                            ),
                        description:
                          item.description ||
                          'Available case status',
                        color:
                          item.color ||
                          'bg-teal-500',
                        active:
                          item.active !==
                          false,
                      };
                    }
                  )
                : [];

            setStatusTypes(
              statuses
            );
          }

          if (
            config.config_key ===
            'risk_rules'
          ) {
            const rules =
              config.config_value &&
              typeof config.config_value ===
                'object'
                ? config.config_value
                : {};

            const criticalRule =
              rules.critical &&
              typeof rules.critical ===
                'object'
                ? rules.critical
                : {};

            const highRule =
              rules.high &&
              typeof rules.high ===
                'object'
                ? rules.high
                : {};

            const moderateRule =
              rules.moderate &&
              typeof rules.moderate ===
                'object'
                ? rules.moderate
                : {};

            const critical = Number(
              criticalRule.minScore ??
                95
            );

            const high = Number(
              highRule.minScore ?? 80
            );

            const moderate = Number(
              moderateRule.minScore ??
                75
            );

            const formattedRiskRules: RiskRule[] =
              [
                {
                  id: 'critical',
                  label: 'Critical',
                  minScore: critical,
                  maxScore: 100,
                  criteria:
                    Array.isArray(
                      criticalRule.criteria
                    )
                      ? criticalRule.criteria
                      : [
                          `Risk score is ${critical} or higher`,
                        ],
                  color: 'text-red-600',
                  bgColor: 'bg-red-50',
                  active:
                    criticalRule.active !== false,
                },
                {
                  id: 'high',
                  label: 'High',
                  minScore: high,
                  maxScore: critical - 1,
                  criteria:
                    Array.isArray(
                      highRule.criteria
                    )
                      ? highRule.criteria
                      : [
                          `Risk score is ${high}–${critical - 1}`,
                        ],
                  color: 'text-orange-600',
                  bgColor: 'bg-orange-50',
                  active:
                    highRule.active !== false,
                },
                {
                  id: 'moderate',
                  label: 'Moderate',
                  minScore: moderate,
                  maxScore: high - 1,
                  criteria:
                    Array.isArray(
                      moderateRule.criteria
                    )
                      ? moderateRule.criteria
                      : [
                          `Risk score is ${moderate}–${high - 1}`,
                        ],
                  color: 'text-amber-600',
                  bgColor: 'bg-amber-50',
                  active:
                    moderateRule.active !== false,
                },
              ];

            setRiskRules(
              formattedRiskRules
            );
          }
        });
      } catch (error) {
        console.error(
          'Failed to load system configuration:',
          error
        );

        alert(
          'Failed to load system configuration.'
        );
      } finally {
        setLoading(false);
      }
    };

    loadConfiguration();
  }, []);

  const toggleCategory = async (id: string) => {
    const currentCategory = caseCategories.find(
      (category) => category.id === id
    );

    if (!currentCategory) {
      return;
    }

    const updatedCategories = caseCategories.map(
      (category) =>
        category.id === id
          ? {
              ...category,
              active: !category.active,
            }
          : category
    );

    setCaseCategories(updatedCategories);

    const categoryConfig =
      updatedCategories.map(
        (category) => ({
          name: category.name,
          code: category.code,
          description: category.description,
          color: category.color,
          active: category.active,
        })
      );

    const success =
      await saveConfig(
        'case_categories',
        categoryConfig
      );

    if (!success) {
      setCaseCategories(caseCategories);
    }
  };

  const toggleStatus = async (
    id: string
  ) => {
    const currentStatus =
      statusTypes.find(
        (status) =>
          status.id === id
      );

    if (!currentStatus) {
      return;
    }

    const nextStatusTypes =
      statusTypes.map(
        (status) =>
          status.id === id
            ? {
                ...status,
                active:
                  !status.active,
              }
            : status
      );

    setStatusTypes(
      nextStatusTypes
    );

    const statusConfig =
      nextStatusTypes.map(
        (status) => ({
          name: status.name,
          code: status.code,
          description:
            status.description,
          color: status.color,
          active: status.active,
        })
      );

    const success =
      await saveConfig(
        'status_types',
        statusConfig
      );

    if (!success) {
      setStatusTypes(
        statusTypes
      );
    }
  };

  const toggleRiskRule = async (id: string) => {
    const updatedRules = riskRules.map(
      (rule) =>
        rule.id === id
          ? {
              ...rule,
              active: !rule.active,
            }
          : rule
    );

    setRiskRules(updatedRules);

    const riskConfig = {
      critical: {
        minScore:
          updatedRules.find(
            (rule) => rule.id === 'critical'
          )?.minScore ?? 90,
        criteria:
          updatedRules.find(
            (rule) => rule.id === 'critical'
          )?.criteria ?? [],
        active:
          updatedRules.find(
            (rule) => rule.id === 'critical'
          )?.active ?? true,
      },

      high: {
        minScore:
          updatedRules.find(
            (rule) => rule.id === 'high'
          )?.minScore ?? 80,
        criteria:
          updatedRules.find(
            (rule) => rule.id === 'high'
          )?.criteria ?? [],
        active:
          updatedRules.find(
            (rule) => rule.id === 'high'
          )?.active ?? true,
      },

      moderate: {
        minScore:
          updatedRules.find(
            (rule) => rule.id === 'moderate'
          )?.minScore ?? 75,
        criteria:
          updatedRules.find(
            (rule) => rule.id === 'moderate'
          )?.criteria ?? [],
        active:
          updatedRules.find(
            (rule) => rule.id === 'moderate'
          )?.active ?? true,
      },
    };

    const success = await saveConfig(
      'risk_rules',
      riskConfig
    );

    if (!success) {
      setRiskRules(riskRules);
    }
  };

  const handleAddCategory =
    async () => {
      if (
        !catForm.name.trim() ||
        !catForm.code.trim()
      ) {
        alert(
          'Category name and code are required.'
        );

        return;
      }

      const newCategory: ConfigCategory =
        {
          id: `category-${Date.now()}`,
          name:
            catForm.name.trim(),
          code:
            catForm.code
              .trim()
              .toUpperCase(),
          description:
            catForm.description.trim(),
          color:
            catForm.color,
          active: true,
        };

      const updatedCategories =
        [
          ...caseCategories,
          newCategory,
        ];

      const categoryConfig =
        updatedCategories.map(
          (category) => ({
            name: category.name,
            code: category.code,
            description:
              category.description,
            color: category.color,
            active: category.active,
          })
        );

      const success =
        await saveConfig(
          'case_categories',
          categoryConfig
        );

      if (!success) {
        return;
      }

      setCaseCategories(
        updatedCategories
      );

      setShowCatModal(false);

      setCatForm({
        name: '',
        code: '',
        description: '',
        color:
          'bg-teal-500',
      });
    };

  const openEditRisk = (
    rule: RiskRule
  ) => {
    setEditRiskId(rule.id);
    setEditRiskScore(
      rule.minScore
    );
    setEditRiskCriteria([
      ...rule.criteria,
    ]);
  };

  const saveRiskCriteria =
    async (
      ruleId: string,
      criteria: string[],
      minScore: number
    ) => {
      if (
        !Number.isFinite(
          minScore
        ) ||
        minScore < 0 ||
        minScore > 100
      ) {
        alert(
          'Risk score must be between 0 and 100.'
        );

        return;
      }

      const updatedRules =
        riskRules.map(
          (rule) =>
            rule.id === ruleId
              ? {
                  ...rule,
                  minScore,
                  criteria,
                }
              : rule
        );

      const critical =
        updatedRules.find(
          (rule) =>
            rule.id ===
            'critical'
        )?.minScore ?? 95;

      const high =
        updatedRules.find(
          (rule) =>
            rule.id === 'high'
        )?.minScore ?? 80;

      const moderate =
        updatedRules.find(
          (rule) =>
            rule.id ===
            'moderate'
        )?.minScore ?? 75;

      if (
        !(
          moderate <
          high &&
          high < critical
        )
      ) {
        alert(
          'Risk thresholds must follow this order: Moderate < High < Critical.'
        );

        return;
      }

      const finalRules =
        updatedRules.map(
          (rule) => {
            if (
              rule.id ===
              'critical'
            ) {
              return {
                ...rule,
                minScore:
                  critical,
                maxScore: 100,
              };
            }

            if (
              rule.id === 'high'
            ) {
              return {
                ...rule,
                minScore: high,
                maxScore:
                  critical - 1,
              };
            }

            if (
              rule.id ===
              'moderate'
            ) {
              return {
                ...rule,
                minScore:
                  moderate,
                maxScore:
                  high - 1,
              };
            }

            return rule;
          }
        );

      const criticalRule =
        finalRules.find(
          (rule) =>
            rule.id ===
            'critical'
        );

      const highRule =
        finalRules.find(
          (rule) =>
            rule.id === 'high'
        );

      const moderateRule =
        finalRules.find(
          (rule) =>
            rule.id ===
            'moderate'
        );

      const riskConfig = {
        critical: {
          minScore: critical,
          criteria:
            finalRules.find(
              (rule) => rule.id === 'critical'
            )?.criteria ?? [],
          active:
            finalRules.find(
              (rule) => rule.id === 'critical'
            )?.active ?? true,
        },

        high: {
          minScore: high,
          criteria:
            finalRules.find(
              (rule) => rule.id === 'high'
            )?.criteria ?? [],
          active:
            finalRules.find(
              (rule) => rule.id === 'high'
            )?.active ?? true,
        },

        moderate: {
          minScore: moderate,
          criteria:
            finalRules.find(
              (rule) => rule.id === 'moderate'
            )?.criteria ?? [],
          active:
            finalRules.find(
              (rule) => rule.id === 'moderate'
            )?.active ?? true,
        },
      };

      const success =
        await saveConfig(
          'risk_rules',
          riskConfig
        );

      if (!success) {
        return;
      }

      setRiskRules(
        finalRules
      );

      setEditRiskId(null);
      setEditRiskScore(0);
      setEditRiskCriteria([]);
    };

  if (loading) {
    return (
      <AppLayout
        title="System Configuration"
        breadcrumb={[
          'Admin',
          'System Config',
        ]}
      >
        <div className="flex items-center justify-center py-20">
          <div className="text-center">
            <i className="ri-loader-4-line animate-spin text-3xl text-teal-500" />

            <p className="text-sm text-gray-500 mt-3">
              Loading system configuration...
            </p>
          </div>
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout
      title="System Configuration"
      breadcrumb={[
        'Admin',
        'System Config',
      ]}
    >
      <div className="flex gap-2 mb-6 flex-wrap">
        {(
          [
            'categories',
            'statuses',
            'risk',
          ] as const
        ).map((tab) => (
          <button
            key={tab}
            onClick={() =>
              setConfigTab(tab)
            }
            className={`px-4 py-2 rounded-xl text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${
              configTab === tab
                ? 'bg-teal-500 text-white'
                : 'bg-white border border-gray-200 text-gray-600 hover:bg-gray-50'
            }`}
          >
            {tab ===
            'categories'
              ? 'Case Categories'
              : tab ===
                'statuses'
              ? 'Status Types'
              : 'Risk Level Rules'}
          </button>
        ))}

        {saved && (
          <span className="text-xs text-teal-600 bg-teal-50 border border-teal-200 px-3 py-2 rounded-xl flex items-center gap-1 ml-auto">
            <i className="ri-checkbox-circle-line" />
            Saved!
          </span>
        )}
      </div>

      {configTab ===
        'categories' && (
        <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
          <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
            <h3 className="font-semibold text-gray-800">
              Case Categories
            </h3>

            <button
              onClick={() =>
                setShowCatModal(
                  true
                )
              }
              className="text-sm bg-teal-500 text-white px-4 py-2 rounded-xl hover:bg-teal-600 transition-colors cursor-pointer whitespace-nowrap flex items-center gap-2"
            >
              <i className="ri-add-line" />
              Add Category
            </button>
          </div>

          <div className="divide-y divide-gray-50">
            {caseCategories.length ===
            0 ? (
              <div className="px-5 py-8 text-center text-sm text-gray-400">
                No case categories found.
              </div>
            ) : (
              caseCategories.map(
                (category) => (
                  <div
                    key={
                      category.id
                    }
                    className="px-5 py-4 flex items-center gap-4"
                  >
                    <div
                      className={`w-3 h-3 rounded-full ${category.color} flex-shrink-0`}
                    />

                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <p className="text-sm font-semibold text-gray-800">
                          {
                            category.name
                          }
                        </p>

                        <span className="text-xs bg-gray-100 text-gray-500 px-2 py-0.5 rounded font-mono">
                          {
                            category.code
                          }
                        </span>
                      </div>

                      <p className="text-xs text-gray-400 mt-0.5">
                        {
                          category.description
                        }
                      </p>
                    </div>

                    <button
                      onClick={() =>
                        toggleCategory(
                          category.id
                        )
                      }
                      className={`w-12 h-6 rounded-full transition-all cursor-pointer relative flex-shrink-0 ${
                        category.active
                          ? 'bg-teal-500'
                          : 'bg-gray-200'
                      }`}
                    >
                      <span
                        className={`absolute top-0.5 w-5 h-5 rounded-full bg-white transition-all ${
                          category.active
                            ? 'left-6'
                            : 'left-0.5'
                        }`}
                      />
                    </button>

                    <span
                      className={`text-xs px-2 py-1 rounded-full font-medium ${
                        category.active
                          ? 'bg-teal-100 text-teal-700'
                          : 'bg-gray-100 text-gray-500'
                      }`}
                    >
                      {category.active
                        ? 'Active'
                        : 'Inactive'}
                    </span>
                  </div>
                )
              )
            )}
          </div>
        </div>
      )}

      {configTab ===
        'statuses' && (
        <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
          <div className="px-5 py-4 border-b border-gray-100">
            <h3 className="font-semibold text-gray-800">
              Case Status Types
            </h3>

            <p className="text-xs text-gray-400 mt-1">
              These statuses are loaded from the system configuration database.
            </p>
          </div>

          <div className="divide-y divide-gray-50">
            {statusTypes.length ===
            0 ? (
              <div className="px-5 py-8 text-center text-sm text-gray-400">
                No status types found.
              </div>
            ) : (
              statusTypes.map(
                (status) => (
                  <div
                    key={
                      status.id
                    }
                    className="px-5 py-4 flex items-center gap-4"
                  >
                    <div
                      className={`w-3 h-3 rounded-full ${status.color} flex-shrink-0`}
                    />

                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <p className="text-sm font-semibold text-gray-800">
                          {
                            status.name
                          }
                        </p>

                        <span className="text-xs bg-gray-100 text-gray-500 px-2 py-0.5 rounded font-mono">
                          {
                            status.code
                          }
                        </span>
                      </div>

                      <p className="text-xs text-gray-400 mt-0.5">
                        {
                          status.description
                        }
                      </p>
                    </div>

                    <button
                      onClick={() =>
                        toggleStatus(
                          status.id
                        )
                      }
                      className={`w-12 h-6 rounded-full transition-all cursor-pointer relative flex-shrink-0 ${
                        status.active
                          ? 'bg-teal-500'
                          : 'bg-gray-200'
                      }`}
                    >
                      <span
                        className={`absolute top-0.5 w-5 h-5 rounded-full bg-white transition-all ${
                          status.active
                            ? 'left-6'
                            : 'left-0.5'
                        }`}
                      />
                    </button>

                    <span
                      className={`text-xs px-2 py-1 rounded-full font-medium ${
                        status.active
                          ? 'bg-teal-100 text-teal-700'
                          : 'bg-gray-100 text-gray-500'
                      }`}
                    >
                      {status.active
                        ? 'Active'
                        : 'Inactive'}
                    </span>
                  </div>
                )
              )
            )}
          </div>
        </div>
      )}

      {configTab ===
        'risk' && (
        <div className="space-y-4">
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-start gap-3">
            <i className="ri-information-line text-amber-600 mt-0.5" />

            <p className="text-sm text-amber-700">
              Risk level rules define
              the criteria used by
              the system to
              automatically classify
              case risk. Click{' '}
              <strong>
                Edit Criteria
              </strong>{' '}
              to modify the
              conditions for each
              level.
            </p>
          </div>

          {riskRules.length ===
          0 ? (
            <div className="bg-white rounded-xl border border-gray-100 px-5 py-8 text-center text-sm text-gray-400">
              No risk rules found.
            </div>
          ) : (
            riskRules.map(
              (rule) => (
                <div
                  key={rule.id}
                  className={`bg-white rounded-xl border-2 overflow-hidden ${
                    rule.active
                      ? 'border-gray-100'
                      : 'border-gray-100 opacity-60'
                  }`}
                >
                  <div
                    className={`px-5 py-3 flex items-center gap-3 ${rule.bgColor}`}
                  >
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <span
                          className={`text-sm font-bold ${rule.color}`}
                        >
                          {
                            rule.label
                          }
                        </span>

                        <span className="text-xs text-gray-500">
                          Score:{' '}
                          {
                            rule.minScore
                          }
                          –
                          {
                            rule.maxScore
                          }
                        </span>
                      </div>
                    </div>

                    <button
                      onClick={() =>
                        openEditRisk(
                          rule
                        )
                      }
                      className="text-xs border border-current px-3 py-1 rounded-lg cursor-pointer whitespace-nowrap flex items-center gap-1 font-medium"
                    >
                      <i className="ri-edit-line" />
                      Edit Criteria
                    </button>

                    <button
                      onClick={() =>
                        toggleRiskRule(
                          rule.id
                        )
                      }
                      className={`w-12 h-6 rounded-full transition-all cursor-pointer relative flex-shrink-0 ${
                        rule.active
                          ? 'bg-teal-500'
                          : 'bg-gray-200'
                      }`}
                    >
                      <span
                        className={`absolute top-0.5 w-5 h-5 rounded-full bg-white transition-all ${
                          rule.active
                            ? 'left-6'
                            : 'left-0.5'
                        }`}
                      />
                    </button>

                    <span
                      className={`text-xs px-2 py-1 rounded-full font-medium ${
                        rule.active
                          ? 'bg-teal-100 text-teal-700'
                          : 'bg-gray-100 text-gray-500'
                      }`}
                    >
                      {rule.active
                        ? 'Active'
                        : 'Inactive'}
                    </span>
                  </div>

                  <div className="px-5 py-4">
                    <p className="text-xs text-gray-500 font-medium mb-2 uppercase tracking-wide">
                      Criteria:
                    </p>

                    <ul className="space-y-1.5">
                      {rule.criteria.map(
                        (
                          criterion,
                          index
                        ) => (
                          <li
                            key={`${rule.id}-criterion-${index}`}
                            className="flex items-start gap-2 text-sm text-gray-700"
                          >
                            <i className="ri-checkbox-circle-line text-teal-500 mt-0.5 flex-shrink-0" />
                            {
                              criterion
                            }
                          </li>
                        )
                      )}
                    </ul>
                  </div>
                </div>
              )
            )
          )}
        </div>
      )}

      {showCatModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl p-6 w-full max-w-md">
            <div className="flex items-center justify-between mb-5">
              <h3 className="text-lg font-bold text-gray-900">
                Add Case Category
              </h3>

              <button
                onClick={() =>
                  setShowCatModal(
                    false
                  )
                }
                className="text-gray-400 hover:text-gray-600 cursor-pointer"
              >
                <i className="ri-close-line text-xl" />
              </button>
            </div>

            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-1.5 block">
                    Category Name{' '}
                    <span className="text-red-500">
                      *
                    </span>
                  </label>

                  <input
                    value={
                      catForm.name
                    }
                    onChange={(e) =>
                      setCatForm({
                        ...catForm,
                        name: e.target
                          .value,
                      })
                    }
                    placeholder="e.g. Physical Abuse"
                    className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm outline-none focus:border-teal-500"
                  />
                </div>

                <div>
                  <label className="text-sm font-medium text-gray-700 mb-1.5 block">
                    Code{' '}
                    <span className="text-red-500">
                      *
                    </span>
                  </label>

                  <input
                    value={
                      catForm.code
                    }
                    onChange={(e) =>
                      setCatForm({
                        ...catForm,
                        code: e.target.value.toUpperCase(),
                      })
                    }
                    placeholder="e.g. PA"
                    className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm outline-none focus:border-teal-500 font-mono"
                  />
                </div>
              </div>

              <div>
                <label className="text-sm font-medium text-gray-700 mb-1.5 block">
                  Description
                </label>

                <textarea
                  value={
                    catForm.description
                  }
                  onChange={(e) =>
                    setCatForm({
                      ...catForm,
                      description:
                        e.target
                          .value,
                    })
                  }
                  rows={2}
                  maxLength={200}
                  className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm outline-none focus:border-teal-500 resize-none"
                />
              </div>

              <div>
                <label className="text-sm font-medium text-gray-700 mb-2 block">
                  Color
                </label>

                <div className="flex gap-2 flex-wrap">
                  {[
                    'bg-red-500',
                    'bg-orange-500',
                    'bg-amber-500',
                    'bg-teal-500',
                    'bg-pink-500',
                    'bg-rose-500',
                    'bg-gray-500',
                  ].map(
                    (color) => (
                      <button
                        key={color}
                        onClick={() =>
                          setCatForm({
                            ...catForm,
                            color,
                          })
                        }
                        className={`w-8 h-8 rounded-full ${color} cursor-pointer transition-all ${
                          catForm.color ===
                          color
                            ? 'ring-2 ring-offset-2 ring-gray-400'
                            : ''
                        }`}
                      />
                    )
                  )}
                </div>
              </div>
            </div>

            <div className="flex gap-3 justify-end mt-5">
              <button
                onClick={() =>
                  setShowCatModal(
                    false
                  )
                }
                className="text-sm border border-gray-200 px-4 py-2 rounded-xl cursor-pointer whitespace-nowrap hover:bg-gray-50"
              >
                Cancel
              </button>

              <button
                onClick={
                  handleAddCategory
                }
                className="text-sm bg-teal-500 text-white px-5 py-2 rounded-xl hover:bg-teal-600 transition-colors cursor-pointer whitespace-nowrap"
              >
                Add Category
              </button>
            </div>
          </div>
        </div>
      )}

      {editRiskId && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl p-6 w-full max-w-lg">
            <div className="flex items-center justify-between mb-5">
              <h3 className="text-lg font-bold text-gray-900">
                Edit Risk Criteria —{' '}
                {
                  riskRules.find(
                    (rule) =>
                      rule.id ===
                      editRiskId
                  )?.label
                }
              </h3>

              <button
                onClick={() =>
                  setEditRiskId(null)
                }
                className="text-gray-400 hover:text-gray-600 cursor-pointer"
              >
                <i className="ri-close-line text-xl" />
              </button>
            </div>

            <div className="mb-4">
              <label className="text-sm font-medium text-gray-700 mb-1.5 block">
                Minimum Risk Score
              </label>

              <input
                type="number"
                min={0}
                max={100}
                value={
                  editRiskScore
                }
                onChange={(e) =>
                  setEditRiskScore(
                    Number(
                      e.target
                        .value
                    )
                  )
                }
                className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm outline-none focus:border-teal-500"
              />

              <p className="text-xs text-gray-400 mt-1">
                Enter a value from 0 to 100.
              </p>
            </div>

            <div className="space-y-2 mb-4 max-h-64 overflow-y-auto">
              {editRiskCriteria.length ===
              0 ? (
                <p className="text-sm text-gray-400">
                  No criteria added.
                </p>
              ) : (
                editRiskCriteria.map(
                  (
                    criterion,
                    index
                  ) => (
                    <div
                      key={`edit-criterion-${index}`}
                      className="flex items-center gap-2"
                    >
                      <input
                        value={
                          criterion
                        }
                        onChange={(
                          e
                        ) => {
                          const updated =
                            [
                              ...editRiskCriteria,
                            ];

                          updated[
                            index
                          ] =
                            e.target.value;

                          setEditRiskCriteria(
                            updated
                          );
                        }}
                        className="flex-1 border border-gray-200 rounded-xl px-3 py-2 text-sm outline-none focus:border-teal-500"
                      />

                      <button
                        onClick={() =>
                          setEditRiskCriteria(
                            (
                              prev
                            ) =>
                              prev.filter(
                                (
                                  _,
                                  idx
                                ) =>
                                  idx !==
                                  index
                              )
                          )
                        }
                        className="text-red-400 hover:text-red-600 cursor-pointer w-8 h-8 flex items-center justify-center"
                      >
                        <i className="ri-close-line" />
                      </button>
                    </div>
                  )
                )
              )}
            </div>

            <div className="flex gap-2 mb-4">
              <input
                value={
                  newCriterion
                }
                onChange={(e) =>
                  setNewCriterion(
                    e.target.value
                  )
                }
                placeholder="Add new criterion..."
                className="flex-1 border border-gray-200 rounded-xl px-3 py-2 text-sm outline-none focus:border-teal-500"
              />

              <button
                onClick={() => {
                  if (
                    newCriterion.trim()
                  ) {
                    setEditRiskCriteria(
                      (prev) => [
                        ...prev,
                        newCriterion.trim(),
                      ]
                    );

                    setNewCriterion(
                      ''
                    );
                  }
                }}
                className="bg-teal-500 text-white px-4 py-2 rounded-xl text-sm cursor-pointer whitespace-nowrap hover:bg-teal-600"
              >
                Add
              </button>
            </div>

            <div className="flex gap-3 justify-end">
              <button
                onClick={() =>
                  setEditRiskId(null)
                }
                className="text-sm border border-gray-200 px-4 py-2 rounded-xl cursor-pointer whitespace-nowrap hover:bg-gray-50"
              >
                Cancel
              </button>

              <button
                onClick={() => {
                  if (!editRiskId) {
                    return;
                  }

                  saveRiskCriteria(
                    editRiskId,
                    editRiskCriteria,
                    editRiskScore
                  );
                }}
                className="text-sm bg-teal-500 text-white px-5 py-2 rounded-xl hover:bg-teal-600 cursor-pointer whitespace-nowrap"
              >
                Save Criteria
              </button>
            </div>
          </div>
        </div>
      )}
    </AppLayout>
  );
}